/**
 * Image optimization utilities for performance enhancement
 */

import homeBgPng from '~/static/assets/images/landing-page/home-bg.png';
import videoVersionPng from '~/static/assets/images/landing-page/video_version.png';
import headerLogo from '~/static/assets/logos/headerLogo.png';

export interface OptimizedImageSources {
  webp?: string;
  fallback: string;
  placeholder?: string;
}

/**
 * Generate optimized image sources with WebP support and fallbacks
 */
export const getOptimizedImageSources = (
  originalPath: string,
  options: {
    generateWebP?: boolean;
    generatePlaceholder?: boolean;
    placeholderQuality?: number;
  } = {},
): OptimizedImageSources => {
  const { generateWebP = true, generatePlaceholder = true } = options;

  // Extract file extension and path
  const lastDotIndex = originalPath.lastIndexOf('.');
  const basePath = originalPath.substring(0, lastDotIndex);
  const extension = originalPath.substring(lastDotIndex + 1).toLowerCase();

  const sources: OptimizedImageSources = {
    fallback: originalPath,
  };

  // Generate WebP version path if supported format
  if (generateWebP && ['png', 'jpg', 'jpeg'].includes(extension)) {
    sources.webp = `${basePath}.webp`;
  }

  // Generate placeholder path (low quality version)
  if (generatePlaceholder) {
    sources.placeholder = `${basePath}-placeholder.${extension}`;
  }

  return sources;
};

/**
 * Preload critical images for better performance
 */
export const preloadImage = (src: string, crossOrigin?: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    if (crossOrigin) {
      link.crossOrigin = crossOrigin;
    }

    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Failed to preload image: ${src}`));

    document.head.appendChild(link);
  });
};

/**
 * Preload multiple critical images
 */
export const preloadCriticalImages = async (imagePaths: string[]): Promise<void> => {
  const preloadPromises = imagePaths.map((path) => preloadImage(path));

  try {
    await Promise.allSettled(preloadPromises);
  } catch (error) {
    console.warn('Some critical images failed to preload:', error);
  }
};

/**
 * Check if WebP format is supported by the browser
 */
export const checkWebpSupport = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      resolve(false);
      return;
    }

    canvas.toBlob((blob) => {
      resolve(blob?.type === 'image/webp');
    }, 'image/webp');
  });
};

/**
 * Generate responsive image srcset for different screen densities
 */
export const generateResponsiveSrcSet = (
  basePath: string,
  extension: string,
  densities: number[] = [1, 2, 3],
): string => {
  return densities
    .map((density) => {
      const suffix = density === 1 ? '' : `@${density}x`;
      return `${basePath}${suffix}.${extension} ${density}x`;
    })
    .join(', ');
};

/**
 * Image loading performance metrics
 */
export class ImagePerformanceMonitor {
  private static instance: ImagePerformanceMonitor;
  private loadTimes: Map<string, number> = new Map();
  private failedLoads: Set<string> = new Set();

  static getInstance(): ImagePerformanceMonitor {
    if (!ImagePerformanceMonitor.instance) {
      ImagePerformanceMonitor.instance = new ImagePerformanceMonitor();
    }
    return ImagePerformanceMonitor.instance;
  }

  recordLoadStart(src: string): void {
    this.loadTimes.set(src, performance.now());
  }

  recordLoadEnd(src: string): void {
    const startTime = this.loadTimes.get(src);
    if (startTime) {
      const loadTime = performance.now() - startTime;
      console.debug(`Image loaded in ${loadTime.toFixed(2)}ms: ${src}`);
      this.loadTimes.delete(src);
    }
  }

  recordLoadError(src: string): void {
    this.failedLoads.add(src);
    console.warn(`Failed to load image: ${src}`);
  }

  getFailedLoads(): string[] {
    return Array.from(this.failedLoads);
  }

  clearMetrics(): void {
    this.loadTimes.clear();
    this.failedLoads.clear();
  }
}

/**
 * Critical images that should be preloaded
 */
export const CRITICAL_IMAGES = [videoVersionPng.src, headerLogo.src, homeBgPng.src];

/**
 * Initialize image optimization on app start
 */
export const initializeImageOptimization = async (): Promise<void> => {
  // Preload critical images
  await preloadCriticalImages(CRITICAL_IMAGES);

  // Add CSS for image loading animations
  const style = document.createElement('style');
  style.textContent = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }

    .lazy-image {
      transition: opacity 0.3s ease-in-out;
      opacity: 0;
    }

    .lazy-image.loaded {
      opacity: 1;
    }

    .lazy-image-placeholder {
      background-color: var(--bootstrap-light, #f8f9fa);
      color: var(--bootstrap-text-muted, #6c757d);
      transition: opacity 0.3s ease-in-out;
    }

    .loading-spinner {
      animation: spin 1s linear infinite;
    }

    .image-error {
      color: var(--bootstrap-danger, #dc3545);
    }
  `;
  document.head.appendChild(style);
};
